import { useState, useEffect, useRef, useImperativeHandle, forwardRef, useMemo, useCallback } from 'react';
import { Button } from './ui/button';
import { 
  Plus,
  Minus,
  CheckSquare,
  Trash2,
  ArrowUpDown,
  Shuffle,
  HelpCircle,
  SquareArrowOutUpRight,
  RotateCcw,
  Upload,
  ExternalLink
} from 'lucide-react';
import MenuAccordionTrigger from './ui/MenuAccordionTrigger';
import { useSpinner, colorOptions, colorSkinOptions, colorEyesOptions, colorHairOptions } from '../lib/SpinnerContext';
import { useParticipants, defaultParticipants } from '../lib/ParticipantsContext';
import { useSettings } from '../lib/SettingsContext';
import { useTheme } from '../lib/ThemeContext';
import { useWheel } from '../lib/WheelContext';
import { menuItems, SidebarMode, MenuItem, gameSubmenuItems, helpSubmenuItems } from '../lib/MenuItems';
import { useTranslation } from 'react-i18next';
import { APP_PROPERTIES } from '../config/appProperties';
import { SpinResult } from '../types';
import { isToday, isYesterday, format } from 'date-fns';
import { ResultsModal } from './modals/ResultsModal';
import { LoadParticipantsModal } from './modals/LoadParticipantsModal';
import { useGame } from '../lib/GameContext';
import { Modal } from './ui/modal';
import { useSidebar } from '../lib/SidebarContext';
import { useLanguage } from '../lib/LanguageContext';
import { useNavigate } from 'react-router-dom';

// Define restricted spinner IDs
const restrictedSpinnerIds = ['beerbottle', 'whiskybottle', 'champagne'];

// Removed tablet-specific constants - tablets now use mobile behavior

interface Participant {
  id: string;
  name: string;
}

type BinarySelectionType = 'yes-no' | 'heads-tails' | 'custom';

interface SidebarProps {
  onCollapseChange?: (collapsed: boolean) => void;
  isCollapsed?: boolean;
  setIsCollapsed?: (collapsed: boolean) => void;
  mobileMode?: boolean;
  mobileVisible?: boolean;
  onClose?: () => void;
  onSpinResult?: (result: SpinResult) => void;
  spinResults: SpinResult[];
  mode: SidebarMode;
  setMode: (mode: SidebarMode) => void;
  onOpenShareModal?: (settings: any) => void;
}

// Add shared style for sidebar title
const sidebarTitleStyle = {
  fontFamily: 'Inter, Helvetica, Arial, sans-serif',
  fontWeight: 700,
  fontSize: 18,
  color: '#fff',
  whiteSpace: 'nowrap',
  letterSpacing: 0.5,
};

const charLimit = APP_PROPERTIES.PARTICIPANT_NAME_CHAR_LIMIT;
const maxParticipants = APP_PROPERTIES.MAX_PARTICIPANTS;
const initialDefaults = APP_PROPERTIES.INITIAL_DEFAULTS;
const MIN_LIMIT = APP_PROPERTIES.RANDOM_NUMBER_MIN;
const MAX_LIMIT = APP_PROPERTIES.RANDOM_NUMBER_MAX;

// Helper functions for deep/shallow equality
function areParticipantsEqual(a: {id: string, name: string}[], b: {id: string, name: string}[]) {
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].id !== b[i].id || a[i].name !== b[i].name) return false;
  }
  return true;
}
function areArraysEqual(a: string[], b: string[]) {
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

// Helper function to load saved participants from localStorage
const loadSavedParticipants = (): Participant[] | null => {
  try {
    const saved = localStorage.getItem('savedParticipants');
    if (!saved) return null;
    
    const parsed = JSON.parse(saved);
    // Validate that parsed data is an array and has the expected structure
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    
    // Verify each participant has id and name properties
    for (const p of parsed) {
      if (!p.id || !p.name) return null;
    }
    
    return parsed;
  } catch (error) {
    console.error('Error loading saved participants:', error);
    // If any error occurs, clear the corrupted localStorage entry
    try {
      localStorage.removeItem('savedParticipants');
    } catch (e) {
      // Ignore any errors on cleanup
    }
    return null;
  }
};

export const Sidebar = forwardRef(function Sidebar({ onCollapseChange, isCollapsed: controlledCollapsed, setIsCollapsed: controlledSetCollapsed, mobileMode, mobileVisible, onClose, onSpinResult, spinResults, mode, setMode, onOpenShareModal }: SidebarProps, ref) {
  const { t, i18n } = useTranslation();
  const { forceUpdate } = useLanguage();
  const navigate = useNavigate();
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const isControlled = controlledCollapsed !== undefined && controlledSetCollapsed !== undefined;
  const isCollapsed = mobileMode ? false : (isControlled ? controlledCollapsed : false);
  const setIsCollapsed = mobileMode ? (() => {}) : (isControlled ? controlledSetCollapsed : () => {});
  const handleCollapseChange = onCollapseChange || (() => {});
  const [newParticipant, setNewParticipant] = useState('');
  const {
    colorCount, setColorCount,
    colorType, setColorType,
    numberSelection, setNumberSelection,
    randomNumberSelection, setRandomNumberSelection,
    minRange, setMinRange,
    maxRange, setMaxRange,
    numberMode, setNumberMode,
  } = useSidebar();
  const [binarySelection, setBinarySelection] = useState<BinarySelectionType>('yes-no');
  const [binaryCount, setBinaryCount] = useState(2);
  const [customBinaryOption1, setCustomBinaryOption1] = useState('Go');
  const [customBinaryOption2, setCustomBinaryOption2] = useState('Stay');
  const [goPlaceholder, setGoPlaceholder] = useState(t('sidebar.submenus.go'));
  const [stayPlaceholder, setStayPlaceholder] = useState(t('sidebar.submenus.stay'));

  // Update placeholders when language changes
  useEffect(() => {
    setGoPlaceholder(t('sidebar.submenus.go'));
    setStayPlaceholder(t('sidebar.submenus.stay'));
  }, [i18n.language, t]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeTab, setActiveTab] = useState<'menu' | 'results'>('menu');
  const idleTimerRef = useRef<number | null>(null);
  const [randomNumberItems, setRandomNumberItems] = useState<string[]>([]);
  
  const { spinnerStyles, selectedSpinner, setSelectedSpinner, isSidebarLocked } = useSpinner();
  const { participants, setParticipants, saveAsDefault, resetParticipants } = useParticipants();
  const { shouldRemoveParticipant, setShouldRemoveParticipant, showAdultSpinners } = useSettings();
  const { currentTheme } = useTheme();
  const { setWheelItems, wheelItems } = useWheel();
  const { currentGameType, setCurrentGameType } = useGame();

  // Removed tablet-specific logic - tablets now use mobile behavior

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      console.log('Sidebar: Language change detected, forcing re-render');
      forceUpdate();
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n, forceUpdate]);

  const [participantsList, setParticipantsList] = useState<string[]>(defaultParticipants.map(p => p.name));
  const [colorList, setColorList] = useState<string[]>(colorOptions.map(c => c.name));
  const [numberList, setNumberList] = useState<string[]>(Array.from({ length: initialDefaults }, (_, i) => (i + 1).toString()));
  const { binaryList, setBinaryList } = useSidebar();

  // Update binary list based on selection and count
  useEffect(() => {
    let baseOptions: string[];
    switch (binarySelection) {
      case 'yes-no':
        baseOptions = ['Yes', 'No'];
        break;
      case 'heads-tails':
        baseOptions = ['Heads', 'Tails'];
        break;
      case 'custom':
        baseOptions = [customBinaryOption1, customBinaryOption2];
        break;
      default:
        baseOptions = ['Yes', 'No'];
    }

    // Create alternating list based on binaryCount
    const alternatingList: string[] = [];
    for (let i = 0; i < binaryCount; i++) {
      alternatingList.push(baseOptions[i % 2]);
    }
    
    setBinaryList(alternatingList);
  }, [binarySelection, binaryCount, customBinaryOption1, customBinaryOption2, setBinaryList]);
  
  // Update colorList based on colorType selection
  useEffect(() => {
    let selectedColorOptions;
    switch (colorType) {
      case 'skin':
        selectedColorOptions = colorSkinOptions;
        break;
      case 'eyes':
        selectedColorOptions = colorEyesOptions;
        break;
      default:
        selectedColorOptions = colorOptions;
    }
    setColorList(selectedColorOptions.map(c => c.name));
    
    // Update colorCount if it exceeds the available colors for the new type
    const maxAvailableColors = Math.min(APP_PROPERTIES.MAX_COLOR, selectedColorOptions.length);
    if (colorCount > maxAvailableColors) {
      setterRefs.current.setColorCount(maxAvailableColors);
    }
  }, [colorType, colorCount]);
  
  const [diceList] = useState<string[]>(['⚀', '⚁', '⚂', '⚃', '⚄', '⚅']);
  const [randomNumberList, setRandomNumberList] = useState<string[]>([]);

  // Store setter functions in refs to avoid closure issues
  const setterRefs = useRef({
    setNumberSelection,
    setRandomNumberSelection,
    setColorCount,
    setMinRange,
    setMaxRange,
    setBinarySelection,
    setNumberMode,
    setMode,
    setParticipants,
    resetParticipants,
    setColorType
  });

  // Update refs when setters change
  useEffect(() => {
    setterRefs.current = {
      setNumberSelection,
      setRandomNumberSelection,
      setColorCount,
      setMinRange,
      setMaxRange,
      setBinarySelection,
      setNumberMode,
      setMode,
      setParticipants,
      resetParticipants,
      setColorType
    };
  }, [setNumberSelection, setRandomNumberSelection, setColorCount, setMinRange, setMaxRange, setBinarySelection, setNumberMode, setMode, setParticipants, resetParticipants, setColorType]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const lastNonFunMode = useRef<SidebarMode>('participants');

  useEffect(() => {
    // If switching to Fun Modes > Just Spinner, do NOT update wheelItems
    if (mode === 'games' && currentGameType === 'just-spinner') {
      return;
    }

    // Otherwise, update lastNonFunMode if not in Fun Modes
    if (mode !== 'games') {
      lastNonFunMode.current = mode;
    }

    let items: Participant[] = [];
    switch (mode) {
      case 'participants':
        items = participants;
        break;
      case 'numbers':
        items = Array.from({ length: numberSelection }, (_, i) => ({ id: `${i + 1}`, name: (i + 1).toString() }));
        break;
      case 'random-numbers':
        const currentMin = parseFloat(String(minRange));
        const currentMax = parseFloat(String(maxRange));
        if (isNaN(currentMin) || isNaN(currentMax) || currentMin > currentMax) {
          items = [];
        } else {
          items = Array.from({ length: randomNumberSelection }, (_, i) => {
            const randomValue = Math.random() * (currentMax - currentMin) + currentMin;
            let displayValue: string;
            if (numberMode === 'integer') {
              displayValue = Math.round(randomValue).toString();
            } else if (numberMode === 'decimal') {
              displayValue = randomValue.toFixed(2);
            } else {
              displayValue = Math.random() > 0.5 ? Math.round(randomValue).toString() : randomValue.toFixed(2);
            }
            return { id: `random-${i + 1}`, name: displayValue };
          });
          setRandomNumberItems(items.map(item => item.name));
        }
        break;
      case 'colors':
        const currentColorOptionsForItems = colorType === 'skin' ? colorSkinOptions : colorType === 'eyes' ? colorEyesOptions : colorType === 'hair' ? colorHairOptions : colorOptions;
        items = currentColorOptionsForItems.slice(0, colorCount).map((color) => ({
          id: `color-${color.name}`,
          name: color.name
        }));
        break;
      case 'binary':
        items = binarySelection === 'yes-no' ? [
          { id: '1', name: 'Yes' },
          { id: '2', name: 'No' }
        ] : binarySelection === 'heads-tails' ? [
          { id: '1', name: 'Heads' },
          { id: '2', name: 'Tails' }
        ] : [
          { id: '1', name: 'Right' },
          { id: '2', name: 'Wrong' }
        ];
        break;
      case 'dice':
        items = diceList.map((name, i) => ({
          id: `${i + 1}`,
          name: name
        }));
        break;
      default:
        items = participants;
    }

    setWheelItems(items);
  }, [mode, colorCount, numberSelection, binarySelection, diceList, participants, setWheelItems, currentGameType, colorType]);

  // Removed tablet-specific idle timeout logic

  // Removed tablet-specific click outside logic

  useEffect(() => {
    if (mobileMode && mobileVisible) {
      setActiveTab('menu');
    }
  }, [mobileMode, mobileVisible]);

  useImperativeHandle(ref, () => {
    return {
      collapseSidebar: () => {
        setIsCollapsed(true);
        setActiveTab('menu');
      },
      getSettings: () => {
        const currentSettings = {
          mode,
          participants: participants.map(p => p.name),
          numberSelection,
          randomNumberSelection,
          minRange,
          maxRange,
          colorCount,
          binarySelection,
          numberMode,
        };
        return currentSettings;
      },
      setSettings: (settings: any) => {
        // 1. Set the raw participant list
        if (settings.participants) {
          setterRefs.current.setParticipants(settings.participants.map((name: string, i: number) => ({ id: `${Date.now()}-${i}`, name })));
        } else {
          setterRefs.current.resetParticipants();
        }
        
        // 2. Set all control values from the loaded settings, with fallbacks
        const loadedMode = settings.mode || 'participants';
        const loadedNumberSelection = settings.numberSelection || initialDefaults;
        const loadedRandomNumberSelection = settings.randomNumberSelection || initialDefaults;
        const loadedColorCount = settings.colorCount || initialDefaults;
        const loadedMinRange = settings.minRange || 1;
        const loadedMaxRange = settings.maxRange || 100;
        const loadedBinarySelection: BinarySelectionType = settings.binarySelection || 'yes-no';
        const loadedNumberMode = settings.numberMode || 'integer';

        // Set each value individually with logging
        setterRefs.current.setNumberSelection(loadedNumberSelection);
        setterRefs.current.setRandomNumberSelection(loadedRandomNumberSelection);
        const currentColorOptionsForInit = colorType === 'skin' ? colorSkinOptions : colorType === 'eyes' ? colorEyesOptions : colorType === 'hair' ? colorHairOptions : colorOptions;
        setterRefs.current.setColorCount(Math.min(loadedColorCount, Math.min(APP_PROPERTIES.MAX_COLOR, currentColorOptionsForInit.length)));
        setterRefs.current.setMinRange(loadedMinRange);
        setterRefs.current.setMaxRange(loadedMaxRange);
        setterRefs.current.setBinarySelection(loadedBinarySelection);
        setterRefs.current.setNumberMode(loadedNumberMode);
        
        // 3. Immediately determine and set the wheel items based on the loaded mode
        let items: Participant[] = [];
        switch (loadedMode) {
          case 'participants':
            // Use the participant list that was just set above
            items = settings.participants ? settings.participants.map((name: string, i: number) => ({ id: `${Date.now()}-${i}`, name })) : [];
            break;
          case 'numbers':
            items = Array.from({ length: loadedNumberSelection }, (_, i) => ({ id: `${i + 1}`, name: (i + 1).toString() }));
            break;
          case 'random-numbers':
            // Generate fresh random numbers based on the loaded settings
            const currentMin = parseFloat(String(loadedMinRange));
            const currentMax = parseFloat(String(loadedMaxRange));
            if (!isNaN(currentMin) && !isNaN(currentMax)) {
              items = Array.from({ length: loadedRandomNumberSelection }, (_, i) => {
                const randomValue = Math.random() * (currentMax - currentMin) + currentMin;
                let displayValue: string;
                if (loadedNumberMode === 'integer') {
                  displayValue = Math.round(randomValue).toString();
                } else if (loadedNumberMode === 'decimal') {
                  displayValue = randomValue.toFixed(2);
                } else {
                  displayValue = Math.random() > 0.5 ? Math.round(randomValue).toString() : randomValue.toFixed(2);
                }
                return { id: `random-${Date.now()}-${i}`, name: displayValue };
              });
              setRandomNumberItems(items.map(item => item.name));
            }
            break;
          case 'colors':
            const loadedColorOptions = colorType === 'skin' ? colorSkinOptions : colorType === 'eyes' ? colorEyesOptions : colorType === 'hair' ? colorHairOptions : colorOptions;
            items = loadedColorOptions.slice(0, loadedColorCount).map((color) => ({
              id: `color-${color.name}`,
              name: color.name
            }));
            break;
          case 'binary':
             const binaryTranslationKeyMap: Record<BinarySelectionType, string> = { 'yes-no': 'yesNo', 'heads-tails': 'headsTails', 'custom': 'custom' };
             const translationKey = binaryTranslationKeyMap[loadedBinarySelection];
             const translated = t(`sidebar.submenus.${translationKey}`);
             const values = translated.split('/').map(s => s.trim());
             items = [ { id: '1', name: values[0] }, { id: '2', name: values[1] } ];
            break;
          case 'dice':
             items = diceList.map((name, i) => ({ id: `${i + 1}`, name }));
             break;
        }

        // 4. Finally, set the mode, which will make the correct menu active
        setterRefs.current.setMode(loadedMode);
      }
    };
  }, [
    // Add all state dependencies so getSettings returns current values
    mode,
    participants,
    numberSelection,
    randomNumberSelection,
    minRange,
    maxRange,
    colorCount,
    binarySelection,
    numberMode,
    // Add function dependencies
    setterRefs,
    initialDefaults,
    colorOptions,
    diceList,
    t,
    setRandomNumberItems
  ]);
  
  const handleAccordionTriggerClick = (menuId: SidebarMode) => {
    if (isSidebarLocked) return;
    if (mode === menuId) return;
    setterRefs.current.setMode(menuId);
  };

  const getActionForMenuItem = (menuItem: MenuItem) => {
    if (menuItem.isExternal && menuItem.href) {
      return () => {
        window.open(menuItem.href, '_blank');
      };
    }
    if (menuItem.id === 'spinnerStyles') {
      return () => {
        if (isCollapsed) {
          toggleSidebar();
          setTimeout(() => setterRefs.current.setMode('spinner-styles'), 50);
        } else if (mode !== 'spinner-styles') {
          setterRefs.current.setMode('spinner-styles');
        }
        // Do not change any pills/wheel data here
      }
    }
    if (menuItem.id === 'help') {
      return () => handleAccordionTriggerClick(menuItem.mode);
    }
    return () => handleAccordionTriggerClick(menuItem.mode);
  };

  const toggleSidebar = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    handleCollapseChange(newCollapsedState);
    // Always reset to menu tab when opening in mobile mode
    if (!newCollapsedState && mobileMode) {
      setActiveTab('menu');
    }
  };

  const generateUniqueParticipantName = (baseName: string): string => {
    const existingNames = participants.map(p => p.name);
    
    if (!existingNames.includes(baseName)) {
      return baseName;
    }
    
    let counter = 1;
    let newName = `${baseName}-${counter}`;
    
    while (existingNames.includes(newName)) {
      counter++;
      newName = `${baseName}-${counter}`;
    }
    
    return newName;
  };

  const addParticipant = () => {
    if (newParticipant.trim() && participants.length < maxParticipants) {
      const trimmedName = newParticipant.trim().substring(0, charLimit);
      const uniqueName = generateUniqueParticipantName(trimmedName);
      const uniqueId = `${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      const newParticipantObj = { id: uniqueId, name: uniqueName };
      
      const updatedParticipants = [newParticipantObj, ...participants];
      setterRefs.current.setParticipants(updatedParticipants);
      saveAsDefault();
      setNewParticipant('');
    }
  };

  const removeParticipant = (id: string) => {
    setterRefs.current.setParticipants(prev => prev.filter(p => p.id !== id));
  };

  const shuffleParticipants = () => {
    setterRefs.current.setParticipants(prev => {
      const shuffled = [...prev];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    });
  };

  const sortParticipants = () => {
    setterRefs.current.setParticipants(prev => [...prev].sort((a, b) => a.name.localeCompare(b.name)));
  };

  const clearParticipants = () => {
    setterRefs.current.setParticipants([]);
  };

  const handleLoadParticipants = (newParticipants: { id: string; name: string }[]) => {
    setterRefs.current.setParticipants(newParticipants);
  };

  const ThreeWayToggle = ({ value, onChange, disabled }: { value: 'integer' | 'both' | 'decimal', onChange: (v: 'integer' | 'both' | 'decimal') => void, disabled?: boolean }) => {
    const handleChange = (newValue: 'integer' | 'both' | 'decimal') => {
      onChange(newValue);
    };

    return (
      <div className={`switch-toggle switch-3 switch-candy w-full mx-auto ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
        <input id="integer" name="number-mode" type="radio" checked={value === 'integer'} onChange={() => handleChange('integer')} disabled={disabled} />
        <label htmlFor="integer" style={{ ...disabled ? { pointerEvents: 'none'} : {}, color: value === 'integer' ? 'white' : '#333333', fontWeight: 'bold' }} className="text-sm">{t('sidebar.submenus.integer')}</label>
        <input id="both" name="number-mode" type="radio" checked={value === 'both'} onChange={() => handleChange('both')} disabled={disabled} />
        <label htmlFor="both" style={{ ...disabled ? { pointerEvents: 'none'} : {}, color: value === 'both' ? 'white' : '#333333', fontWeight: 'bold' }} className="text-sm">{t('sidebar.submenus.both')}</label>
        <input id="decimal" name="number-mode" type="radio" checked={value === 'decimal'} onChange={() => handleChange('decimal')} disabled={disabled} />
        <label htmlFor="decimal" style={{ ...disabled ? { pointerEvents: 'none'} : {}, color: value === 'decimal' ? 'white' : '#333333', fontWeight: 'bold' }} className="text-sm">{t('sidebar.submenus.decimal')}</label>
        <a style={disabled ? { pointerEvents: 'none'} : {}}></a>
      </div>
    );
  };

  // Add local state for min/max input fields
  const [minRangeInput, setMinRangeInput] = useState(minRange.toString());
  const [maxRangeInput, setMaxRangeInput] = useState(maxRange.toString());

  useEffect(() => { setMinRangeInput(minRange.toString()); }, [minRange]);
  useEffect(() => { setMaxRangeInput(maxRange.toString()); }, [maxRange]);

  const handleMinChange = (val: string) => {
    if (val === '' || val === '-') {
      setMinRangeInput(val);
      return;
    }
    let num = Number(val);
    if (isNaN(num)) return;
    if (num < MIN_LIMIT) num = MIN_LIMIT;
    if (num > MAX_LIMIT) num = MAX_LIMIT;
    setMinRangeInput(num.toString());
    if (num > maxRange) num = maxRange;
    setMinRange(num);
  };
  const handleMaxChange = (val: string) => {
    if (val === '' || val === '-') {
      setMaxRangeInput(val);
      return;
    }
    let num = Number(val);
    if (isNaN(num)) return;
    if (num < MIN_LIMIT) num = MIN_LIMIT;
    if (num > MAX_LIMIT) num = MAX_LIMIT;
    setMaxRangeInput(num.toString());
    if (num < minRange) num = minRange;
    setMaxRange(num);
  };
  const handleMinBlur = () => {
    if (minRangeInput === '' || minRangeInput === '-') setMinRangeInput(minRange.toString());
  };
  const handleMaxBlur = () => {
    if (maxRangeInput === '' || maxRangeInput === '-') setMaxRangeInput(maxRange.toString());
  };

  const renderAccordionContent = (menuItem: MenuItem) => {
    if (isCollapsed) return null;
    switch (menuItem.mode) {
      case 'spinner-styles':
        return (
          <div className="w-full p-2">
            <div className="space-y-0.5 mt-1">
              {spinnerStyles.filter(spinner => showAdultSpinners || !restrictedSpinnerIds.includes(spinner.id)).map((spinner) => (
                <div key={spinner.id} className={`text-[0.97rem] cursor-pointer select-none px-2 py-1 rounded-md leading-tight ${selectedSpinner.id === spinner.id ? 'bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300 font-semibold' : 'hover:bg-gray-500/10'}`} onClick={() => !isSidebarLocked && setSelectedSpinner(spinner)} style={{ color: selectedSpinner.id === spinner.id ? undefined : currentTheme.textPrimary, pointerEvents: isSidebarLocked ? 'none' : 'auto', marginBottom: 2 }}>
                    {t(`sidebar.spinnerStylesTranslations.${spinner.id}`)}
                  </div>
                ))}
            </div>
          </div>
        );
      case 'participants':
        return (
          <div className="w-full p-2">
            <div className="space-y-2">
              <div className="flex items-center gap-1 min-w-0">
                <input 
                  type="text" 
                  placeholder={t('participant.enterName', { charLimit })} 
                  value={newParticipant} 
                  maxLength={charLimit} 
                  onChange={(e) => setNewParticipant(e.target.value)} 
                  onKeyDown={(e) => { if (e.key === 'Enter') addParticipant(); }} 
                  className="flex-1 min-w-0 px-2 py-1.5 border rounded-md text-base dark:bg-gray-700 dark:border-gray-600" 
                  style={{ borderColor: currentTheme.border, color: currentTheme.textPrimary }} 
                  disabled={isSidebarLocked || participants.length >= maxParticipants} 
                />
                <Button 
                  size="icon" 
                  onClick={addParticipant} 
                  className="bg-blue-500 hover:bg-blue-600 text-white flex-shrink-0 w-7 h-7 min-w-[1.75rem]" 
                  disabled={isSidebarLocked || !newParticipant.trim()} 
                  data-testid="add-participant-button"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="max-h-48 overflow-y-auto space-y-0 pr-1">
                {participants.map((p) => (
                  <div key={p.id} className="flex items-center justify-between py-0 pl-1 pr-0 rounded-md hover:bg-gray-500/5">
                    <span className="text-base truncate" style={{ color: currentTheme.textSecondary }}>{p.name}</span>
                    <Button variant="ghost" size="icon" onClick={() => removeParticipant(p.id)} className="text-red-500 hover:text-red-600 flex-shrink-0 w-6 h-6" disabled={isSidebarLocked}>
                      <Minus className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="text-base text-gray-500 mt-1">
                {t('participant.numberOfEntries', { current: participants.length, max: maxParticipants })}
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <Button onClick={shuffleParticipants} className="bg-blue-500 hover:bg-blue-600 text-white h-7 text-xs" disabled={isSidebarLocked} data-testid="shuffle-button"><Shuffle className="h-3.5 w-3.5 mr-1" aria-hidden="true" />{t('sidebar.submenus.shuffle')}</Button>
                <Button onClick={sortParticipants} className="bg-blue-500 hover:bg-blue-600 text-white h-7 text-xs" disabled={isSidebarLocked} data-testid="sort-button"><ArrowUpDown className="h-3.5 w-3.5 mr-1" aria-hidden="true" />{t('sidebar.submenus.sort')}</Button>
                <Button onClick={clearParticipants} className="bg-red-500 hover:bg-red-600 text-white h-7 text-xs" disabled={isSidebarLocked} data-testid="clear-button"><Trash2 className="h-3.5 w-3.5 mr-1" aria-hidden="true" />{t('sidebar.submenus.clear')}</Button>
                <Button onClick={() => {
                  if (onOpenShareModal) {
                    const settings = {
                      mode,
                      participants: participants.map(p => p.name),
                      numberSelection,
                      randomNumberSelection,
                      minRange,
                      maxRange,
                      colorCount,
                      binarySelection,
                      numberMode,
                    };
                    onOpenShareModal(settings);
                  } else {
                    // Fallback to old behavior if no callback provided
                    saveAsDefault();
                  }
                }} className="bg-green-500 hover:bg-green-600 text-white h-7 text-xs" disabled={isSidebarLocked} data-testid="save-button"><CheckSquare className="h-3.5 w-3.5 mr-1" aria-hidden="true" />{t('sidebar.submenus.save')}</Button>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-1.5">
                <Button 
                  className="h-7 text-xs transition-colors duration-200 reset-button" 
                  onClick={() => {
                    const savedParticipants = loadSavedParticipants();
                    console.log('Reset button clicked - saved participants:', savedParticipants);
                    if (savedParticipants) {
                      setterRefs.current.setParticipants(savedParticipants);
                    } else {
                      // Fallback to default participants if no saved data
                      setterRefs.current.resetParticipants();
                    }
                  }} 
                  style={{ 
                    borderColor: currentTheme.border, 
                    color: currentTheme.textSecondary, 
                    border: `1px solid ${currentTheme.border}` 
                  }} 
                  disabled={isSidebarLocked} 
                  data-testid="reset-button"
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                  {t('sidebar.submenus.reset')}
                </Button>
                <Button 
                  className="h-7 text-xs text-black transition-colors duration-200 load-button" 
                  onClick={() => setShowLoadParticipantsModal(true)} 
                  style={{ backgroundColor: '#FFE9A2' }} 
                  disabled={isSidebarLocked} 
                  data-testid="load-button"
                >
                  <Upload className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                  {t('sidebar.submenus.load')}
                </Button>
              </div>
            </div>
          </div>
        );
      case 'dice':
        return (<div className="w-full p-2"><div className="space-y-2"><div className="text-base text-gray-600 dark:text-gray-400" style={{color: currentTheme.textSecondary}}>{t('game.spin')} !!</div></div></div>);
      case 'numbers':
        return (
          <div className="w-full p-2">
            <div className="space-y-2">
              <label htmlFor="number-count" className="block text-base font-medium" style={{color: currentTheme.textSecondary}}>{t('sidebar.submenus.numberOfEntries')}: {numberSelection}</label>
              <input type="range" id="number-count" value={numberSelection} onChange={(e) => setterRefs.current.setNumberSelection(parseInt(e.target.value))} min={2} max={maxParticipants} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700" style={{ borderColor: currentTheme.border, color: currentTheme.textPrimary }} disabled={isSidebarLocked} />
            </div>
          </div>
        );
      case 'random-numbers':
        return (
          <div className="w-full p-2 text-xs max-h-60 overflow-y-auto">
            <div className="space-y-2 w-full max-w-[240px]">
              <div>
                <label htmlFor="random-count" className="block font-medium text-base" style={{color: currentTheme.textSecondary}}>{t('sidebar.submenus.numberOfEntries')}: {randomNumberSelection}</label>
                <input type="range" id="random-count" value={randomNumberSelection} onChange={(e) => setterRefs.current.setRandomNumberSelection(parseInt(e.target.value))} min={2} max={maxParticipants} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700" disabled={isSidebarLocked} />
              </div>
              <div className="flex space-x-2 mt-2">
                <div className="w-1/2">
                  <label htmlFor="min-range" className="block font-medium text-base" style={{color: currentTheme.textSecondary}}>{t('sidebar.submenus.min')}</label>
                  <input type="text" maxLength={10} value={minRangeInput} onChange={e => handleMinChange(e.target.value)} onBlur={handleMinBlur} className="w-full p-1 border rounded-md text-base dark:bg-gray-700 dark:border-gray-600" disabled={isSidebarLocked} />
                </div>
                <div className="w-1/2">
                  <label htmlFor="max-range" className="block font-medium text-base" style={{color: currentTheme.textSecondary}}>{t('sidebar.submenus.max')}</label>
                  <input type="text" maxLength={10} value={maxRangeInput} onChange={e => handleMaxChange(e.target.value)} onBlur={handleMaxBlur} className="w-full p-1 border rounded-md text-base dark:bg-gray-700 dark:border-gray-600" disabled={isSidebarLocked} />
                </div>
              </div>
              <div className="mt-2">
                <label className="block font-medium mb-1 text-base" style={{color: currentTheme.textSecondary}}>{t('sidebar.submenus.numberType')}</label>
                <ThreeWayToggle value={numberMode} onChange={setNumberMode} disabled={isSidebarLocked} />
              </div>
            </div>
          </div>
        );
      case 'colors':
        const currentColorOptions = colorType === 'skin' ? colorSkinOptions : colorType === 'eyes' ? colorEyesOptions : colorType === 'hair' ? colorHairOptions : colorOptions;
        return (
          <div className="w-full p-2">
            <div className="space-y-2">
              {/* Color Type Radio Buttons */}
              <div className="space-y-2">
                <div className="space-y-1">
                  {[
                    { value: 'random', label: t('sidebar.submenus.randomColors') },
                    { value: 'skin', label: t('sidebar.submenus.skinColor') },
                    { value: 'eyes', label: t('sidebar.submenus.eyesColor') },
                    { value: 'hair', label: t('sidebar.submenus.hairColor') }
                  ].map(({ value, label }) => (
                    <label
                      key={value}
                      className={`flex items-center gap-2 cursor-pointer px-2 py-1 rounded transition-all ${colorType === value ? 'bg-blue-100 text-blue-700 font-semibold' : ''}`}
                      style={{ minHeight: '32px', alignItems: 'center' }}
                    >
                      <input
                        type="radio"
                        name="color-type"
                        value={value}
                        checked={colorType === value}
                        onChange={() => setColorType(value as 'random' | 'skin' | 'eyes' | 'hair')}
                        disabled={isSidebarLocked}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 checked:bg-blue-600 checked:border-blue-600"
                      />
                      <span className="text-sm" style={{color: currentTheme.textSecondary}}>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <label htmlFor="color-count" className="block text-base font-medium" style={{color: currentTheme.textSecondary}}>{t('sidebar.submenus.numberOfColors')}: {colorCount}</label>
              <input type="range" id="color-count" value={colorCount} onChange={(e) => setterRefs.current.setColorCount(parseInt(e.target.value))} min={2} max={Math.min(APP_PROPERTIES.MAX_COLOR, currentColorOptions.length)} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700" style={{ borderColor: currentTheme.border, color: currentTheme.textPrimary }} disabled={isSidebarLocked} />
              
              {/* Color Swatch Display */}
              <div className="mt-3">
                <div className="grid grid-cols-6 gap-2 max-w-full">
                  {currentColorOptions.slice(0, colorCount).map((color) => (
                    <div 
                      key={`${colorType}-${color.name}`} 
                      className="w-8 h-8 border-2 border-gray-400 dark:border-gray-500 rounded cursor-pointer hover:scale-110 transition-transform" 
                      style={{ backgroundColor: color.name }}
                      title={`${color.label} (${color.name})`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      case 'binary':
        return (
          <div className="w-full p-2">
            <div className="space-y-2">
              <div className="space-y-1">
                {(['yes-no', 'heads-tails', 'custom'] as const).map(value => (
                  <label
                    key={value}
                    className={`flex items-center gap-2 cursor-pointer px-2 py-1 rounded transition-all ${binarySelection === value ? 'bg-blue-100 text-blue-700 font-semibold' : ''}`}
                    style={{ minHeight: '32px', alignItems: 'center' }}
                  >
                    <input
                      type="radio"
                      name="binary-selection"
                      value={value}
                      checked={binarySelection === value}
                      onChange={() => setterRefs.current.setBinarySelection(value)}
                      disabled={isSidebarLocked}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 checked:bg-blue-600 checked:border-blue-600"
                    />
                    <span className="text-sm">
                      {t(`sidebar.submenus.${{ 'yes-no': 'yesNo', 'heads-tails': 'headsTails', 'custom': 'custom'}[value]}`)}
                    </span>
                  </label>
                ))}
              </div>

                            {/* Custom binary options */}
              {binarySelection === 'custom' && (
                <div className="space-y-2 mt-3">
                  <div className="flex space-x-2">
                    <div className="w-1/2">
                      <input
                        key={`go-input-${i18n.language}`}
                        type="text"
                        placeholder={goPlaceholder}
                        value={customBinaryOption1}
                        onChange={(e) => setCustomBinaryOption1(e.target.value)}
                        className="w-full px-2 py-1 border rounded-md text-sm"
                        style={{ 
                          backgroundColor: APP_PROPERTIES.BINARY_PILLS_COLOR_EVEN, 
                          borderColor: '#bfdbfe',
                          color: APP_PROPERTIES.BINARY_PILLS_TEXT_COLOR_EVEN
                        }}
                        disabled={isSidebarLocked}
                      />
                    </div>
                    <div className="w-1/2">
                      <input
                        key={`stay-input-${i18n.language}`}
                        type="text"
                        placeholder={stayPlaceholder}
                        value={customBinaryOption2}
                        onChange={(e) => setCustomBinaryOption2(e.target.value)}
                        className="w-full px-2 py-1 border rounded-md text-sm"
                        style={{ 
                          backgroundColor: APP_PROPERTIES.BINARY_PILLS_COLOR_ODD, 
                          borderColor: '#fecaca',
                          color: APP_PROPERTIES.BINARY_PILLS_TEXT_COLOR_ODD
                        }}
                        disabled={isSidebarLocked}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Binary count slider */}
              <div className="mt-3">
                <label htmlFor="binary-count" className="block text-base font-medium" style={{color: currentTheme.textSecondary}}>
                  {t('sidebar.submenus.numberOfEntries')}: {binaryCount}
                </label>
                <input 
                  type="range" 
                  id="binary-count" 
                  value={binaryCount} 
                  onChange={(e) => setBinaryCount(parseInt(e.target.value))} 
                  min={2} 
                  max={APP_PROPERTIES.MAX_NUM} 
                  step={2}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700" 
                  style={{ borderColor: currentTheme.border, color: currentTheme.textPrimary }} 
                  disabled={isSidebarLocked} 
                />
              </div>
            </div>
          </div>
        );
      case 'games':
        return (
          <div className="w-full p-2">
            <div className="space-y-0.5 mt-1">
              {[{ id: 'just-spinner' },
                { id: 'trivia' },
                { id: 'would-you-rather' },
                { id: 'which-one-you-are' },
                { id: 'truth-dare' }
              ].map((game) => (
                <button
                  key={game.id}
                  className={`text-[0.97rem] cursor-pointer select-none px-2 py-1 rounded-md leading-tight w-full text-left ${
                    currentGameType === game.id
                      ? 'bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300 font-semibold'
                      : 'hover:bg-gray-500/10'
                  }`}
                  onClick={() => setCurrentGameType(game.id as import('../lib/MenuItems').GameType)}
                >
                  {t(`sidebar.games.${game.id}`)}
                </button>
              ))}
            </div>
            {/* Remove Winner in a thin, rounded, light grey box */}
            <div style={{ display: 'flex', flexDirection: 'row', gap: 8, marginTop: 10 }}>
              <Button
                variant="default"
                className="bg-blue-500 hover:bg-blue-600 text-white flex items-center gap-1 px-2"
                style={{ fontWeight: 500, fontSize: 13, borderRadius: 7, padding: '2px 8px', height: 28, minHeight: 28, display: 'flex', alignItems: 'center' }}
                asChild
              >
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', width: '100%', height: 28, margin: 0, fontSize: 13 }}>
                  <input
                    type="checkbox"
                    id="removeWinnerFunModes"
                    checked={shouldRemoveParticipant}
                    onChange={e => setShouldRemoveParticipant(e.target.checked)}
                    className="custom-checkbox"
                    style={{ margin: 0, width: 16, height: 16 }}
                  />
                  {t('sidebar.submenus.removeWinner')}
                </label>
              </Button>
            </div>
          </div>
        );
      case 'help':
        return (
          <div className="w-full p-2">
            <div className="space-y-0.5 mt-1">
              {helpSubmenuItems.map((item) => (
                <button
                  key={item.id}
                  className={`text-[0.97rem] cursor-pointer select-none px-2 py-1 rounded-md leading-tight w-full text-left ${
                    mode === 'help' && item.id === 'help-center'
                      ? 'bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300 font-semibold'
                      : 'hover:bg-gray-500/10'
                  }`}
                  onClick={() => {
                    if (item.isExternal && item.href) {
                      window.open(item.href, '_blank');
                    } else if (item.href) {
                      navigate(item.href);
                    }
                  }}
                  style={{ color: mode === 'help' && item.id === 'help-center' ? undefined : currentTheme.textPrimary }}
                >
                  <div className="flex items-center justify-between w-full">
                    <span>{(() => {
                      switch(item.id) {
                        case 'help-center': return t('sidebar.helpSubmenu.help-center');
                        case 'blogs': return t('sidebar.helpSubmenu.blogs');
                        case 'privacy-policy': return t('sidebar.helpSubmenu.privacy-policy');
                        case 'terms': return t('sidebar.helpSubmenu.terms');
                        default: return item.id;
                      }
                    })()}</span>
                    {item.isExternal && <ExternalLink className="h-3 w-3" />}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
    }
  };

  const renderTabs = () => (
    <div className="flex border-b mb-1" style={{ borderColor: currentTheme.border }}>
      <button className={`flex-1 py-1 px-2 text-sm font-medium transition-colors ${activeTab === 'menu' ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}`} onClick={() => setActiveTab('menu')}>
        {t('sidebar.tabs.menu')}
      </button>
      <button className={`flex-1 py-1 px-2 text-sm font-medium transition-colors ${activeTab === 'results' ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}`} onClick={() => setActiveTab('results')}>
        {t('sidebar.tabs.results')}
      </button>
    </div>
  );

  const getColorDisplayName = (hexOrKey: string) => {
    const currentColorOptionsForDisplay = colorType === 'skin' ? colorSkinOptions : colorType === 'eyes' ? colorEyesOptions : colorType === 'hair' ? colorHairOptions : colorOptions;
    const color = currentColorOptionsForDisplay.find(c => c.name === hexOrKey || c.key === hexOrKey);
    if (color) {
      if (colorType === 'skin') {
        return t(`skinColors.${color.key}`);
      } else if (colorType === 'eyes') {
        return t(`eyeColors.${color.key}`);
      } else if (colorType === 'hair') {
        return t(`hairColors.${color.key}`);
      } else {
        return t(`colors.${color.key}`);
      }
    }
    return hexOrKey;
  };
  
  const resultsContainerRef = useRef<HTMLDivElement>(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showLoadParticipantsModal, setShowLoadParticipantsModal] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // Function to get results by type
  const getResultsByType = (type: SidebarMode) => {
    return spinResults.filter(result => result.type === type);
  };

  // Function to format results for display
  const formatResults = (type: SidebarMode) => {
    const results = getResultsByType(type);
    if (results.length === 0) return null;
    
    return (
      <div className="text-xs text-gray-500 mt-1">
        {t('sidebar.results.winners')}: {results.map(r => r.winner).join(', ')}
      </div>
    );
  };

  // Helper function to get proper spinner name
  const getSpinnerName = (result: SpinResult) => {
    // If spinner has a custom name, use it
    if (result.spinnerName && result.spinnerName.trim()) {
      return result.spinnerName;
    }
    
    // Default fallback
    return t('sidebar.results.untitled', 'Untitled');
  };

  // Helper function to get game type suffix
  const getGameTypeSuffix = (result: SpinResult) => {
    // If there's a game type (Fun Mode was active), show it
    if (result.gameType) {
      return `: ${t(`sidebar.games.${result.gameType}`, result.gameType)}`;
    }
    
    // For non-participants (dice/numbers/colors/binary), show "Just Spinner"
    if (['dice', 'numbers', 'random-numbers', 'colors', 'binary'].includes(result.type)) {
      return `: ${t('sidebar.games.justSpinner', 'Just Spinner')}`;
    }
    
    // For participants without game type, show "Just Spinner"
    if (result.type === 'participants') {
      return `: ${t('sidebar.games.justSpinner', 'Just Spinner')}`;
    }
    
    return '';
  };

  // Function to render results tab content
  const renderResultsContent = () => {
    const allowedTypes: SidebarMode[] = ['participants', 'dice', 'numbers', 'random-numbers', 'colors', 'binary', 'games'];
    const sortedResults = [...spinResults]
      .filter(result => allowedTypes.includes(result.type))
      .sort((a, b) => b.timestamp - a.timestamp);



    if (sortedResults.length === 0) {
      return (
        <div className="text-base text-gray-500">
          {t('sidebar.results.empty')}
        </div>
      );
    }

    // Group by date, then by spinner name, then by type
    const groupedByDateSpinnerAndType = sortedResults.reduce((acc, result) => {
      const date = new Date(result.timestamp);
      let dayLabel = '';
      if (isToday(date)) {
        dayLabel = t('sidebar.results.today', 'Today');
      } else if (isYesterday(date)) {
        dayLabel = t('sidebar.results.yesterday', 'Yesterday');
      } else {
        dayLabel = format(date, 'PPP');
      }
      
      const spinnerName = getSpinnerName(result);
      const gameTypeSuffix = getGameTypeSuffix(result);
      const fullSpinnerName = spinnerName + gameTypeSuffix;
      const typeKey = result.type;
      
      if (!acc[dayLabel]) acc[dayLabel] = {};
      if (!acc[dayLabel][fullSpinnerName]) acc[dayLabel][fullSpinnerName] = {};
      if (!acc[dayLabel][fullSpinnerName][typeKey]) acc[dayLabel][fullSpinnerName][typeKey] = [];
      acc[dayLabel][fullSpinnerName][typeKey].push(result);
      return acc;
    }, {} as Record<string, Record<string, Record<string, SpinResult[]>>>);

    const typeTitleMap: Record<string, string> = {
      participants: t('sidebar.participants', 'Participants'),
      numbers: t('sidebar.numbers', 'Numbers'),
      'random-numbers': t('sidebar.randomNumbers', 'Random Numbers'),
      colors: t('sidebar.colors', 'Colors'),
      binary: t('sidebar.binary', 'Binary'),
      dice: t('sidebar.dice', 'Dice'),
    };

    const resultsJsx = (
      <div className="space-y-4">
        {Object.entries(groupedByDateSpinnerAndType).map(([day, spinnerGroups]) => (
          <div key={day} className="mb-4">
            {/* Date header - shown only once per day */}
            <div className="flex items-center mb-2">
              <span className="text-sm font-semibold" style={{ color: currentTheme.textSecondary }}>{day}</span>
              <div className="flex-grow border-t ml-2" style={{ borderColor: currentTheme.border }}></div>
            </div>
            
            {/* Spinner groups */}
            {Object.entries(spinnerGroups).map(([fullSpinnerName, typeGroups]) => (
              <div key={fullSpinnerName} className="mb-3 ml-1">
                {/* Spinner name header */}
                <div className="text-sm font-bold mb-1" style={{ color: currentTheme.textPrimary }}>
                  {fullSpinnerName}
                </div>
                
                {/* Type groups */}
                {Object.entries(typeGroups).map(([type, results]) => (
                  <div key={type} className="mb-2 ml-3">
                    {/* Type header */}
                    <div className="text-xs font-semibold mb-1" style={{ color: currentTheme.textPrimary }}>
                      {typeTitleMap[type] || type}
                    </div>
                    
                    {/* Results list */}
                    <div className="space-y-1">
                      {results.map((result, index) => (
                        <div key={`${result.id}-${index}`} className="flex items-center text-sm ml-4" style={{ minHeight: 20 }}>
                          <span
                            style={{
                              flex: 1,
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: currentTheme.textSecondary,
                              fontSize: result.type === 'dice' ? '1.5rem' : '0.875rem',
                              marginRight: 8,
                            }}
                          >
                            {result.type === 'colors' ? getColorDisplayName(result.winner) : result.winner}
                          </span>
                          <span className="text-xs text-gray-500" style={{ width: 80, textAlign: 'right', marginRight: 8 }}>
                            {format(result.timestamp, 'hh:mm:ss a')}
                          </span>
                          <span style={{ width: 20, display: 'flex', justifyContent: 'center' }}>
                            {result.gameResultCandidate ? (
                              <span style={{ fontSize: '0.75rem' }}>
                                {result.gameResultCandidate === '🔘' ? (
                                  <span style={{ filter: 'brightness(1.2) contrast(1.1) saturate(0.7)' }}>⚪</span>
                                ) : (
                                  result.gameResultCandidate
                                )}
                              </span>
                            ) : (
                              <span style={{ opacity: 0 }}>&#9679;</span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    );

    return (
      <>
        <div ref={resultsContainerRef} className="max-h-[calc(100vh-250px)] overflow-hidden">
          {resultsJsx}
        </div>

        {/* Spacer to push the button to the bottom */}
        <div style={{ flexGrow: 1 }} />

        <div className="w-full px-2 pb-4 pt-2" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, background: 'inherit' }}>
          <button
            onClick={() => setShowResultsModal(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition"
            style={{ border: 'none', outline: 'none' }}
          >
            <SquareArrowOutUpRight size={18} className="inline-block" />
            {t('sidebar.results.showAll', 'All Results')}
          </button>
        </div>

        <ResultsModal
          isOpen={showResultsModal}
          onClose={() => setShowResultsModal(false)}
          results={sortedResults}
        />
      </>
    );
  };



  return (
    <>
      <style>{`
        /* Reset button hover effect */
        .reset-button {
          background-color: #e5e7eb !important;
        }
        .reset-button:hover {
          background-color: #d1d5db !important;
        }
        .dark .reset-button {
          background-color: #4b5563 !important;
        }
        .dark .reset-button:hover {
          background-color: #6b7280 !important;
        }
        
        /* Load button hover effect */
        .load-button:hover {
          background-color: #F8D775 !important;
        }
        
        .sidebar-container input[type=range] {
          -webkit-appearance: none !important;
          appearance: none !important;
          width: 100%;
          height: 4px;
          background: #888 !important;
          border-radius: 2px;
          outline: none;
        }
        .sidebar-container input[type=range]::-webkit-slider-thumb {
          -webkit-appearance: none !important;
          appearance: none !important;
          background: #1976d2 !important;
          border: 2px solid #1976d2 !important;
          box-shadow: 0 0 2px #1976d2 !important;
          width: 16px !important;
          height: 16px !important;
          border-radius: 50% !important;
          display: block;
        }
        .sidebar-container input[type=range]:focus::-webkit-slider-thumb {
          box-shadow: 0 0 0 3px #90caf9 !important;
        }
        .sidebar-container input[type=range]::-moz-range-thumb {
          background: #1976d2 !important;
          border: 2px solid #1976d2 !important;
          box-shadow: 0 0 2px #1976d2 !important;
          width: 16px !important;
          height: 16px !important;
          border-radius: 50% !important;
        }
        .sidebar-container input[type=range]:focus::-moz-range-thumb {
          box-shadow: 0 0 0 3px #90caf9 !important;
        }
        .sidebar-container input[type=range]::-ms-thumb {
          background: #1976d2 !important;
          border: 2px solid #1976d2 !important;
          box-shadow: 0 0 2px #1976d2 !important;
          width: 16px !important;
          height: 16px !important;
          border-radius: 50% !important;
        }
        .sidebar-container input[type=range]:focus::-ms-thumb {
          box-shadow: 0 0 0 3px #90caf9 !important;
        }
        .sidebar-container input[type=range]:active::-webkit-slider-thumb,
        .sidebar-container input[type=range]:hover::-webkit-slider-thumb {
          background: #1565c0 !important;
          border-color: #1565c0 !important;
        }
        .sidebar-container input[type=range]:active::-moz-range-thumb,
        .sidebar-container input[type=range]:hover::-moz-range-thumb {
          background: #1565c0 !important;
          border-color: #1565c0 !important;
        }
        .sidebar-container input[type=range]:active::-ms-thumb,
        .sidebar-container input[type=range]:hover::-ms-thumb {
          background: #1565c0 !important;
          border-color: #1565c0 !important;
        }
        /* Firefox */
        .sidebar-container input[type=range]::-moz-range-track {
          background: #888 !important;
          height: 4px;
          border-radius: 2px;
        }
        /* IE */
        .sidebar-container input[type=range]::-ms-fill-lower,
        .sidebar-container input[type=range]::-ms-fill-upper {
          background: #888 !important;
          border-radius: 2px;
        }
        /* Remove outline for Firefox */
        .sidebar-container input[type=range]:focus {
          outline: none;
        }
        .custom-checkbox {
          position: relative;
          width: 16px;
          height: 16px;
          appearance: none;
          background: #222;
          border: 2px solid #4f8cff;
          border-radius: 4px;
          cursor: pointer;
          outline: none;
          transition: border 0.2s;
          vertical-align: middle;
        }
        .custom-checkbox:checked {
          background:rgb(0, 0, 0);
          border-color:rgb(0, 0, 0);
        }
        .custom-checkbox:checked::after {
          content: '';
          position: absolute;
          left: 4px;
          top: 0px;
          width: 4px;
          height: 8px;
          border: solid #fff;
          border-width: 0 2.5px 2.5px 0;
          transform: rotate(45deg);
          display: block;
        }
      `}</style>
      <div className={`sidebar-container flex flex-col z-40 ${mobileMode ? `fixed left-0 top-0 w-80 h-full shadow-2xl border-r transition-transform duration-300 ease-in-out ${mobileVisible ? 'translate-x-0' : '-translate-x-full'}` : 'fixed left-0 top-0 h-[calc(100vh-54px)] hidden md:flex'}`} style={{ backgroundColor: currentTheme.paper, borderColor: currentTheme.border, width: mobileMode ? '280px' : (isCollapsed ? '5rem' : '16rem'), height: mobileMode ? '100%' : undefined, cursor: isCollapsed ? 'pointer' : 'default', pointerEvents: mobileMode && !mobileVisible ? 'none' : 'auto' }} onClick={() => { if (!mobileMode && isCollapsed && window.innerWidth >= 1024) { setIsCollapsed(false); setActiveTab('menu'); } }}>
        <div className={`flex items-center border-b px-4 h-[54px] flex-shrink-0 bg-[#1e2434]`} style={{ borderColor: currentTheme.border, justifyContent: 'flex-start', cursor: !isCollapsed ? 'pointer' : 'default', }} onClick={e => { if (!mobileMode && !isCollapsed && window.innerWidth >= 1024) { setIsCollapsed(true); setActiveTab('menu'); e.stopPropagation(); } }}>
          <div className={`relative flex items-center w-full`} aria-label={isCollapsed ? "Expand menu" : "Collapse menu"}>
            <img src="/logo.png" alt="Spin Logo" style={{ width: 40, height: 40, objectFit: 'contain', marginRight: 8 }} />
            <span style={{ ...sidebarTitleStyle, marginLeft: 2, maxWidth: isCollapsed ? 0 : 160, display: (isCollapsed || mobileMode) ? 'none' : 'inline-block', alignSelf: 'center', }}>{t('header.title')}</span>
          </div>
          {mobileMode && onClose && (<button onClick={onClose} className="ml-auto text-white text-xl px-2 py-1">×</button>)}
        </div>
        <div className={`flex-grow overflow-y-auto p-3 min-h-0`} style={{ color: currentTheme.textPrimary, backgroundColor: currentTheme.paper, minHeight: '100%' }}>
          {(!isCollapsed || mobileMode) && renderTabs()}
          <div className="w-full space-y-0">
            {activeTab === 'menu' ? (
              <>
                {menuItems.map((item) => (
                  <div key={item.id} className="border-0 mb-0.5">
                    <MenuAccordionTrigger
                      icon={item.icon}
                      title={item.id === 'games' ? t('sidebar.funModes') : item.id === 'help' ? t('sidebar.help') : t(`sidebar.${item.id}`)}
                      isSelected={mode === item.mode}
                      isCollapsed={isCollapsed}
                      onClick={getActionForMenuItem(item)}
                      disabled={isSidebarLocked}
                    />
                    {(!isCollapsed || mobileMode) && mode === item.mode && renderAccordionContent(item)}
                  </div>
                ))}
              </>
            ) : (
              <div className="results-tab">
                {renderResultsContent()}
              </div>
            )}
          </div>
        </div>
      </div>
              <LoadParticipantsModal
          isOpen={showLoadParticipantsModal}
          onClose={() => setShowLoadParticipantsModal(false)}
          onLoadParticipants={handleLoadParticipants}
          currentParticipants={participants}
        />
    </>
  );
});